import type { FlagCode, FlagColorKey } from "@/types/container-flag";

/**
 * Reading a container flag through the tenant's colour code.
 *
 * Kept pure — codes and a colour in, a string or a class out — because the same
 * answer has to come out on screen, in the label preview and in the printed
 * document, and three copies of this would drift.
 */

/** Screen swatches. Tokens, so a red dot is the same red as the rest of the app. */
const FLAG_CLASSES: Record<FlagColorKey, string> = {
  red: "bg-(--color-danger)",
  orange: "bg-(--color-warning)",
  yellow: "bg-(--color-warning)",
  green: "bg-(--color-status-active)",
  blue: "bg-(--brand-blue)",
  purple: "bg-(--color-accent-purple)",
};

/**
 * Print swatches, as literal ink.
 *
 * The printed document is built as a standalone HTML string with no stylesheet,
 * so it cannot reach a CSS variable. Orange and yellow are separated here — on
 * screen they share a warning token, but a label is the thing a cook matches
 * against a lid, and two lids are not the same lid.
 */
const FLAG_HEX: Record<FlagColorKey, string> = {
  red: "#dc2626",
  orange: "#ea580c",
  yellow: "#eab308",
  green: "#16a34a",
  blue: "#2563eb",
  purple: "#9333ea",
};

export function flagColorClass(color: string): string {
  return FLAG_CLASSES[color as FlagColorKey] ?? "bg-(--gray-400)";
}

export function flagColorHex(color: string): string {
  return FLAG_HEX[color as FlagColorKey] ?? "#9ca3af";
}

/**
 * What a stored flag means here.
 *
 * A colour the tenant has since retired comes back as itself rather than as
 * nothing: the container in the walk-in still has a blue lid, and printing
 * nothing would quietly drop something the cook can see with their own eyes.
 */
export function flagMeaning(
  codes: readonly FlagCode[] | undefined,
  flag: string | null | undefined,
): string | null {
  if (!flag) return null;
  return codes?.find((c) => c.color === flag)?.meaning ?? flag;
}

/**
 * The colours a form may offer.
 *
 * Only colours the tenant has given a meaning — a dot nobody can read is worse
 * than no dot, because it looks like information. A colour already saved on this
 * row is offered even if it has been retired, because a picker that cannot show
 * its own value silently rewrites it on the next save.
 */
export function flagOptions(
  codes: readonly FlagCode[] | undefined,
  current?: string | null,
): FlagCode[] {
  const options = [...(codes ?? [])];
  if (current && !options.some((c) => c.color === current)) {
    options.push({ color: current as FlagColorKey, meaning: current });
  }
  return options;
}
